import { useState } from "react";
import { Star, ThumbsUp, ThumbsDown, Circle, BookOpen, X } from "lucide-react";

/**
 * Different media types use different rating scales — this widget picks the
 * right UI per type and calls onChange with the flexible shape the backend
 * expects: {scale, value}. Pass rating={null} to render an empty state.
 *
 * Scales:
 *   5star   → 5 half-star clicks (value: 0..5)                 [movie, tvshow, music, book, article, video]
 *   ternary → Make Again / Maybe / Never Again                 [recipe]
 *   status  → Finished / DNF (secondary companion for books)   [book, article]
 */
export function scaleForType(type) {
  if (type === "recipe") return "ternary";
  return "5star";
}

export default function Rating({ type, value, onChange, testId = "rating" }) {
  const scale = scaleForType(type);
  if (scale === "ternary") return <TernaryRating value={value} onChange={onChange} testId={testId} />;
  return <StarRating value={value} onChange={onChange} testId={testId} />;
}

function StarRating({ value, onChange, testId }) {
  const [hover, setHover] = useState(0);
  const current = (value && value.scale === "5star") ? Number(value.value || 0) : 0;
  const display = hover || current;

  function set(v) {
    if (v === current) onChange(null);   // Toggle off if tapping the same star again
    else onChange({ scale: "5star", value: v });
  }

  return (
    <div className="flex items-center gap-1.5" data-testid={testId} onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map(v => {
        const filled = v <= Math.floor(display);
        const half = !filled && v - 0.5 <= display;
        return (
          <button
            key={v}
            type="button"
            onClick={() => set(v)}
            onMouseEnter={() => setHover(v)}
            aria-label={`${v} star${v === 1 ? "" : "s"}`}
            data-testid={`${testId}-star-${v}`}
            className="p-0.5 hover:scale-110 active:scale-95 transition-transform"
          >
            <Star
              size={18}
              strokeWidth={1.4}
              className={filled ? "fill-[var(--bronze)] text-[var(--bronze)]" : half ? "fill-[var(--bronze)]/50 text-[var(--bronze)]" : "text-[var(--ink-light)]"}
            />
          </button>
        );
      })}
      {current > 0 && (
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label="Clear rating"
          data-testid={`${testId}-clear`}
          className="ml-2 text-[var(--ink-light)] hover:text-[var(--terracotta)]"
        >
          <X size={12} strokeWidth={1.6}/>
        </button>
      )}
    </div>
  );
}

const TERNARY_OPTIONS = [
  { key: "make-again",   label: "Make again",   Icon: ThumbsUp,   color: "var(--sage)" },
  { key: "maybe",        label: "Maybe",        Icon: Circle,     color: "var(--bronze)" },
  { key: "never-again",  label: "Never again",  Icon: ThumbsDown, color: "var(--terracotta)" },
];

function TernaryRating({ value, onChange, testId }) {
  const current = value?.scale === "ternary" ? value.value : null;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testId}>
      {TERNARY_OPTIONS.map(opt => {
        const active = current === opt.key;
        const Icon = opt.Icon;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(active ? null : { scale: "ternary", value: opt.key })}
            data-testid={`${testId}-${opt.key}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-cinzel text-[10px] tracking-[0.2em] uppercase transition-all"
            style={{
              borderColor: active ? opt.color : "rgba(42,30,20,0.16)",
              background: active ? `${opt.color}18` : "transparent",
              color: active ? opt.color : "var(--ink-dusty)",
            }}
          >
            <Icon size={12} strokeWidth={1.6}/>
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/** Optional companion for books/articles — small status pill next to the star row.
 *  Value here is a bare string ("finished" | "dnf" | null), NOT the {scale,value}
 *  rating shape — the parent stores this in a separate top-level `status` field
 *  so a star rating and a "finished" mark can coexist. */
export function StatusPill({ value, onChange, testId = "status" }) {
  const current = value || null;
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      {[["finished","Finished", BookOpen], ["dnf","DNF", X]].map(([k, label, Icon]) => {
        const active = current === k;
        return (
          <button key={k} type="button"
            onClick={() => onChange(active ? null : k)}
            data-testid={`${testId}-${k}`}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full border font-cinzel text-[9px] tracking-[0.22em] uppercase transition-colors ${
              active
                ? "border-[var(--bronze)] bg-[var(--paper-aged)] text-[var(--ink)]"
                : "border-[rgba(42,30,20,0.16)] text-[var(--ink-dusty)] hover:bg-[var(--paper-aged)]"
            }`}>
            <Icon size={10} strokeWidth={1.6}/>
            {label}
          </button>
        );
      })}
    </div>
  );
}
